import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ListAltIcon from '@mui/icons-material/ListAlt';
import axios from 'axios';

export default function Vacaciones() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadorId, setTrabajadorId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [regOpen, setRegOpen] = useState(false);
  const [regForm, setRegForm] = useState({ trabajador_id: '', anio_record: new Date().getFullYear(), fecha_inicio_record: '', fecha_fin_record: '', dias_ganados: 30 });

  const [goceOpen, setGoceOpen] = useState(false);
  const [goceVacId, setGoceVacId] = useState(null);
  const [goceForm, setGoceForm] = useState({ tipo: 'GOCE', fecha_inicio: '', fecha_fin: '', dias: '', importe: '', observaciones: '' });

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleVacId, setDetalleVacId] = useState(null);
  const [detalleRows, setDetalleRows] = useState([]);

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) { setTrabajadores([]); return; }
    axios.get('/api/rrhh/trabajadores', { params: { empresa_id: empresaId, estado: 'ACTIVO' } })
      .then(r => setTrabajadores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/vacaciones', { params: { empresa_id: empresaId, trabajador_id: trabajadorId || undefined } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [empresaId, trabajadorId]);

  useEffect(() => { cargar(); }, [cargar]);

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;
  const trabObj = trabajadores.find(t => t.id === trabajadorId) || null;

  const handleCrearRegistro = async () => {
    try {
      await axios.post('/api/rrhh/vacaciones', { ...regForm, empresa_id: empresaId });
      setRegOpen(false);
      setSuccess('Registro vacacional creado.');
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al crear'); }
  };

  const handleAgregarGoce = async () => {
    try {
      await axios.post('/api/rrhh/vacaciones/detalle', { ...goceForm, vacacion_id: goceVacId });
      setGoceOpen(false);
      setSuccess(`${goceForm.tipo === 'GOCE' ? 'Goce' : 'Venta'} de vacaciones registrado.`);
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al registrar'); }
  };

  const handleVerDetalle = async (vacId) => {
    setDetalleVacId(vacId);
    setDetalleOpen(true);
    try {
      const r = await axios.get(`/api/rrhh/vacaciones/${vacId}/detalle`);
      setDetalleRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar detalle'); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1200 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>Control de Vacaciones</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ p: 2, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={empresas} getOptionLabel={o => o?.razon_social || ''}
              value={empresaObj}
              onChange={(_, v) => { setEmpresaId(v ? v.empresa_id : ''); setTrabajadorId(''); }}
              isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
              renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <Autocomplete
              options={trabajadores}
              getOptionLabel={o => o ? `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}` : ''}
              value={trabObj}
              onChange={(_, v) => setTrabajadorId(v ? v.id : '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={p => <TextField {...p} label="Trabajador (opcional)" size="small" sx={{ minWidth: '20em' }} />}
              sx={{ minWidth: '20em' }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setRegForm({ trabajador_id: '', anio_record: new Date().getFullYear(), fecha_inicio_record: '', fecha_fin_record: '', dias_ganados: 30 }); setRegOpen(true); }} disabled={!empresaId}>
              Nuevo Récord Vacacional
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Trabajador</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Año Récord</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ganados</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Gozados</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Vendidos</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Disponibles</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Progreso</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay registros vacacionales</TableCell></TableRow>
                  ) : rows.map(r => {
                    const usados = r.dias_gozados + r.dias_vendidos;
                    const pct = Math.round((usados / r.dias_ganados) * 100);
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{r.trabajador_nombre}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.num_doc}</Typography>
                        </TableCell>
                        <TableCell align="center"><Chip size="small" label={r.anio_record} /></TableCell>
                        <TableCell align="center">{r.dias_ganados}</TableCell>
                        <TableCell align="center" sx={{ color: r.dias_gozados > 0 ? '#1976d2' : 'text.secondary' }}>{r.dias_gozados}</TableCell>
                        <TableCell align="center" sx={{ color: r.dias_vendidos > 0 ? '#f57c00' : 'text.secondary' }}>{r.dias_vendidos}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={`${r.dias_disponibles} días`} color={r.dias_disponibles > 0 ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} />
                            <Typography variant="caption">{pct}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Registrar goce/venta">
                            <IconButton size="small" color="primary" onClick={() => { setGoceVacId(r.id); setGoceForm({ tipo: 'GOCE', fecha_inicio: '', fecha_fin: '', dias: '', importe: '', observaciones: '' }); setGoceOpen(true); }}>
                              <BeachAccessIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver movimientos">
                            <IconButton size="small" onClick={() => handleVerDetalle(r.id)}>
                              <ListAltIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialog Nuevo Récord */}
      <Dialog open={regOpen} onClose={() => setRegOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">Nuevo Récord Vacacional</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              options={trabajadores}
              getOptionLabel={o => o ? `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}` : ''}
              value={trabajadores.find(t => t.id === regForm.trabajador_id) || null}
              onChange={(_, v) => setRegForm(f => ({ ...f, trabajador_id: v ? v.id : '' }))}
              renderInput={p => <TextField {...p} label="Trabajador" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <TextField label="Año Récord" size="small" type="number" value={regForm.anio_record} onChange={e => setRegForm(f => ({ ...f, anio_record: e.target.value }))} sx={{ minWidth: '10em' }} />
            <TextField label="Inicio del Récord" size="small" type="date" InputLabelProps={{ shrink: true }} value={regForm.fecha_inicio_record} onChange={e => setRegForm(f => ({ ...f, fecha_inicio_record: e.target.value }))} sx={{ minWidth: '16em' }} />
            <TextField label="Fin del Récord" size="small" type="date" InputLabelProps={{ shrink: true }} value={regForm.fecha_fin_record} onChange={e => setRegForm(f => ({ ...f, fecha_fin_record: e.target.value }))} sx={{ minWidth: '16em' }} />
            <TextField label="Días Ganados" size="small" type="number" value={regForm.dias_ganados} onChange={e => setRegForm(f => ({ ...f, dias_ganados: e.target.value }))} sx={{ minWidth: '10em' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCrearRegistro} disabled={!regForm.trabajador_id}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Goce/Venta */}
      <Dialog open={goceOpen} onClose={() => setGoceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Registrar Goce / Venta de Vacaciones</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select label="Tipo" size="small" value={goceForm.tipo} onChange={e => setGoceForm(f => ({ ...f, tipo: e.target.value }))} sx={{ minWidth: '16em' }}>
              <MenuItem value="GOCE">Goce de vacaciones</MenuItem>
              <MenuItem value="VENTA">Venta de vacaciones (hasta 15 días)</MenuItem>
            </TextField>
            <TextField label="Fecha Inicio" size="small" type="date" InputLabelProps={{ shrink: true }} value={goceForm.fecha_inicio} onChange={e => setGoceForm(f => ({ ...f, fecha_inicio: e.target.value }))} sx={{ minWidth: '16em' }} />
            <TextField label="Fecha Fin" size="small" type="date" InputLabelProps={{ shrink: true }} value={goceForm.fecha_fin} onChange={e => setGoceForm(f => ({ ...f, fecha_fin: e.target.value }))} sx={{ minWidth: '16em' }} />
            <TextField label="N° Días" size="small" type="number" value={goceForm.dias} onChange={e => setGoceForm(f => ({ ...f, dias: e.target.value }))} sx={{ minWidth: '10em' }} />
            {goceForm.tipo === 'VENTA' && (
              <TextField label="Importe Pago (S/)" size="small" type="number" value={goceForm.importe} onChange={e => setGoceForm(f => ({ ...f, importe: e.target.value }))} sx={{ minWidth: '16em' }} />
            )}
            <TextField label="Observaciones" size="small" value={goceForm.observaciones} onChange={e => setGoceForm(f => ({ ...f, observaciones: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoceOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAgregarGoce} disabled={!goceForm.dias || !goceForm.fecha_inicio}>Registrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detalle */}
      <Dialog open={detalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">Movimientos Vacacionales</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Inicio</TableCell>
                  <TableCell>Fin</TableCell>
                  <TableCell align="center">Días</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detalleRows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin movimientos</TableCell></TableRow>
                ) : detalleRows.map(d => (
                  <TableRow key={d.id} hover>
                    <TableCell><Chip size="small" label={d.tipo} color={d.tipo === 'GOCE' ? 'primary' : 'warning'} /></TableCell>
                    <TableCell sx={{ fontSize: '0.82em' }}>{d.fecha_inicio?.slice(0,10)}</TableCell>
                    <TableCell sx={{ fontSize: '0.82em' }}>{d.fecha_fin?.slice(0,10)}</TableCell>
                    <TableCell align="center">{d.dias}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{d.importe > 0 ? `S/ ${Number(d.importe).toFixed(2)}` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={() => setDetalleOpen(false)}>Cerrar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
